import { ref, computed } from 'vue';

// This detects whether we're in a Nuxt environment or not
const isNuxt = typeof process !== 'undefined' && process.server !== undefined;

// Add debug logging
const debug = (...args: any[]) => {
  // console.log('[useI18n Debug]', ...args);
};

export function useI18n() {
  // In a real setup, this would be imported from your main app's i18n instance
  // But for demonstration, we'll create a simple implementation

  if (isNuxt) {
    // For Nuxt, use nuxt-i18n
    const nuxtApp = getNuxtApp();
    const i18n = nuxtApp?.$i18n;

    if (i18n) {
      console.log('[useI18n] Using Nuxt i18n instance', i18n.locale.value);
      return {
        t: i18n.t,
        locale: computed(() => i18n.locale.value),
        setLocale: (newLocale: string) => i18n.setLocale(newLocale),
      };
    }
  }

  // Fallback or Vite implementation
  const mapLocale = (browserLocale: string): string => {
    // Extract the language part (e.g., 'en' from 'en-GB')
    const language = browserLocale.split('-')[0].toLowerCase();

    // Map to our available translations
    if (language === 'nl') return 'nl-NL';
    // Default to English
    return 'en-EN';
  };

  const browserLocale = typeof navigator !== 'undefined' ? navigator?.language : 'en';
  const initialLocale = mapLocale(browserLocale || 'en') || 'en-EN';

  console.log(
    '[useI18n] Detected browserLocale:',
    browserLocale,
    '-> initialLocale:',
    initialLocale
  );

  const locale = ref(initialLocale);
  const isLoaded = ref(false); // Add loading state
  let messages: Record<string, Record<string, string>> = {};

  // Available translations
  const availableTranslations = ['en-EN', 'nl-NL'];

  // Dynamic import of translation files - requires a bundler that supports dynamic imports
  const loadTranslations = async (loc: string) => {
    // Make sure we're loading a translation that exists
    const translationKey = availableTranslations.includes(loc) ? loc : 'en-EN';

    try {
      // We'll try to import the translation file dynamically
      const translationModule = await import(`../translations/${translationKey}.ts`);
      messages[translationKey] = translationModule.default;

      // If we had to use a fallback, update the locale ref
      if (translationKey !== loc) {
        console.warn(`Translation for ${loc} not found, using ${translationKey} instead`);
        locale.value = translationKey;
      }
    } catch (error) {
      console.error(`Failed to load translations for ${translationKey}`, error);
      // If we somehow failed to load even the fallback English translation,
      // we should provide an empty object to prevent further errors
      messages[translationKey] = {};
    }
  };

  // Translation function with variable interpolation
  const t = (key: string, vars?: Record<string, any>) => {
    // Return a placeholder while translations are loading
    if (!isLoaded.value || !messages[locale.value]) {
      console.warn(
        '[useI18n] Translations not loaded yet for',
        locale.value,
        'Returning key:',
        key
      );
      return key;
    }

    const keys = key.split('.');
    let result: any = messages[locale.value];

    for (const k of keys) {
      if (result && typeof result === 'object') {
        result = result[k];
      } else {
        console.warn('[useI18n] Translation key not found:', key);
        return key;
      }
    }

    if (typeof result === 'string') {
      // Interpolate variables if provided
      if (vars && typeof vars === 'object') {
        return result.replace(/\{(\w+)\}/g, (match, varName) => {
          return vars[varName] !== undefined ? vars[varName] : match;
        });
      }
      return result;
    }
    return key;
  };

  // Initialize with the current locale
  loadTranslations(locale.value).then(() => {
    isLoaded.value = true;
  });

  // Set a new locale and load its translations
  const setLocale = async (newLocale: string) => {
    // Only try to load translations if the locale is one we support
    // or map it to one we do support
    const mappedLocale = availableTranslations.includes(newLocale)
      ? newLocale
      : mapLocale(newLocale);

    if (!messages[mappedLocale]) {
      await loadTranslations(mappedLocale);
    }
    locale.value = mappedLocale;
  };

  const result = {
    t,
    locale: computed(() => locale.value),
    setLocale,
    availableLocales: computed(() => availableTranslations),
    isLoaded: computed(() => isLoaded.value), // Expose loading state
  };

  return result;
}

// Type-safe utility for Nuxt detection
function getNuxtApp() {
  try {
    // @ts-ignore - This will be available in Nuxt environment
    const app = typeof useNuxtApp === 'function' ? useNuxtApp() : undefined;
    return app;
  } catch (error) {
    return undefined;
  }
}
